import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

import { BaseButton, type ChildRenderProps, type ExtendedProps } from './BaseButton';
import { type ButtonAction, ButtonSize, ButtonVariant } from './types';

export function Button({
    active,
    block = false,
    children,
    centered = block,
    className,
    disabled,
    ref,
    iconClassName,
    iconLeft,
    iconRight,
    onClick,
    size = 'medium',
    type = 'button',
    variant = 'secondary',
    labelClassName,
    ...props
}: Button.Props) {
    function renderButton({
        children: renderChildren,
        'data-has-text-content': hasTextContent,
        ...attributes
    }: ChildRenderProps) {
        const ariaLabel = props['aria-label'];

        return (
            <button
                {...props}
                aria-label={ariaLabel}
                onClick={onClick}
                ref={ref}
                type={type}
                {...attributes}
            >
                {renderChildren}
            </button>
        );
    }

    return (
        <BaseButton
            active={active ?? false}
            block={block}
            centered={centered}
            className={className}
            disabled={disabled ?? false}
            render={renderButton}
            size={size}
            variant={variant}
            iconClassName={iconClassName}
            iconLeft={iconLeft}
            iconRight={iconRight}
            labelClassName={labelClassName}
        >
            {children}
        </BaseButton>
    );
}

type ConditionalProps =
    | {
          children?: never;
      }
    | {
          children: ReactNode;
      };

export namespace Button {
    export type Props = Omit<
        ExtendedProps<ButtonHTMLAttributes<HTMLButtonElement>>,
        'children' | 'onClick'
    > &
        ConditionalProps & {
            ref?: Ref<HTMLButtonElement>;
            onClick?: ButtonAction;
        };

    export type Action = ButtonAction;

    export type Variant = ButtonVariant;
    export const Variant = ButtonVariant;

    export type Size = ButtonSize;
    export const Size = ButtonSize;
}
